import React from 'react';

import { Modal, Button, Title, Group, Stack, Text } from '@mantine/core';
import { AxiosError, AxiosResponse } from 'axios';
import { useStaffTracking } from 'hooks/mixpanel/useStaffTracking';
import { UseMutationResult, useQueryClient } from 'react-query';
import {
  AthletePackageRetrieve,
  AthletePackageUpdate,
  AthletePackageUpdateResponse,
  ErrorMessage,
} from 'types';
import notificationHandler from 'utils/notifications';

import { autoPaySuccessMessage } from './chainPropagationMessages';

interface IAutoPayModalProps {
  opened: boolean;
  athletePackage: AthletePackageRetrieve;
  closeHandler: () => void;
  updateAthletePackageMutation: UseMutationResult<
    AxiosResponse<AthletePackageUpdateResponse>,
    AxiosError<ErrorMessage>,
    AthletePackageUpdate
  >;
}

const AutoPayModal = ({
  opened,
  athletePackage,
  closeHandler,
  updateAthletePackageMutation,
}: IAutoPayModalProps): JSX.Element => {
  const { successNotification, errorNotification } = notificationHandler();
  const { trackAthletePackageAutoPayUpdated } = useStaffTracking();
  const queryClient = useQueryClient();

  const handleSave = (): void => {
    const autoPay = !athletePackage.autoPay;
    const payload: AthletePackageUpdate = {
      action: 'auto_pay',
      athletePackageId: athletePackage.athletePackageId,
      autoRenew: athletePackage.autoRenew,
      autoPay,
      paymentMethod: athletePackage.paymentMethod,
      nonStripePaymentMethod: athletePackage.nonStripePaymentMethod,
      athleteStatusOnStart: athletePackage.athleteStatusOnStart,
      athleteStatusOnEnd: athletePackage.athleteStatusOnEnd,
    };
    updateAthletePackageMutation.mutate(payload, {
      onError: error => {
        errorNotification('Athlete package update failed', error.response?.data.message);
      },
      onSuccess: response => {
        const propagation = response.data.chainPropagation;
        queryClient.invalidateQueries(['invoices']);
        trackAthletePackageAutoPayUpdated(
          athletePackage.athletePackageId,
          autoPay,
          propagation?.packagesUpdated ?? 0,
          propagation?.packagesSkipped ?? 0,
          propagation?.skippedReasons ?? [],
        );
        successNotification('Athlete package update success', autoPaySuccessMessage(autoPay, propagation));
        closeHandler();
      },
    });
  };

  return (
    <Modal
      opened={opened}
      onClose={closeHandler}
      size='auto'
      centered
      title={<Title order={4}>{athletePackage.autoPay ? 'Disable Auto-Pay' : 'Enable Auto-Pay'}</Title>}
    >
      <Stack p='md'>
        {/*
          Names every status the walk reads, which is CURRENT_STATUSES: active, upcoming and on
          hold. The change travels both ways along the renewal chain, so opening this from an
          upcoming renewal also writes the term the member is on right now. Saying "upcoming"
          alone would understate that.
        */}
        {athletePackage.autoPay ? (
          <Text color='dimmed' size='sm'>
            This will disable Auto-Pay for this package, the other active, upcoming and on-hold packages
            linked to it, and their upcoming invoices.
          </Text>
        ) : (
          <Stack spacing={0}>
            <Text color='dimmed' size='sm'>
              This will enable Auto-Pay for this package, the other active, upcoming and on-hold packages
              linked to it, and their upcoming invoices,
            </Text>
            <Text color='dimmed' size='sm'>
              where the payment method is not cash or check.
            </Text>
          </Stack>
        )}
      </Stack>
      <Group position='right' pt='md'>
        <Button
          variant='light'
          onClick={closeHandler}
          size='xs'
          radius='md'
          disabled={updateAthletePackageMutation.isLoading}
        >
          Cancel
        </Button>
        <Button size='xs' radius='md' loading={updateAthletePackageMutation.isLoading} onClick={handleSave}>
          Save
        </Button>
      </Group>
    </Modal>
  );
};

export default AutoPayModal;
